import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import './IOSDownloadPage.css';
import { auth, db, functions } from '../firebase';
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

function IOSDownloadPage() {
    const { t } = useTranslation();
    const { requestId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [imageUrl, setImageUrl] = useState('');
    const [stravaId, setStravaId] = useState('');

    // User authentication state
    const [currentUser, setCurrentUser] = useState(null);
    const [requestData, setRequestData] = useState(null);

    // Watermark check state
    const [isCheckingWatermark, setIsCheckingWatermark] = useState(false);
    const [hasExistingWatermark, setHasExistingWatermark] = useState(false);
    const [showWatermarkButton, setShowWatermarkButton] = useState(false);
    const [watermarkCheckStatus, setWatermarkCheckStatus] = useState(null);
    const [watermarkCheckError, setWatermarkCheckError] = useState(null);

    // Dialog state
    const [showDialog, setShowDialog] = useState(false);
    const [dialogContent, setDialogContent] = useState({
        type: 'success', // 'success', 'error', 'info'
        title: '',
        message: '',
        icon: ''
    });

    // Helper function to show custom dialog instead of alert
    const showCustomDialog = (type, title, message, icon = '') => {
        setDialogContent({ type, title, message, icon });
        setShowDialog(true);
    };

    // Helper function to check if map was generated before watermark implementation
    const isLegacyMap = () => {
        if (!requestData) return false;

        // Watermark feature was implemented on June 26, 2025
        const watermarkImplementationDate = new Date('2025-06-26T00:00:00Z');

        // Check various possible date fields
        const generationDate =
            requestData.createdAt?.toDate?.() ||
            requestData.timestamp?.toDate?.() ||
            requestData.generatedAt?.toDate?.() ||
            requestData.generationStartTime?.toDate?.() ||
            (requestData.createdAt instanceof Date ? requestData.createdAt : null) ||
            (requestData.timestamp instanceof Date ? requestData.timestamp : null) ||
            (requestData.generatedAt instanceof Date ? requestData.generatedAt : null) ||
            (requestData.generationStartTime instanceof Date ? requestData.generationStartTime : null);

        if (!generationDate) return false;

        return generationDate < watermarkImplementationDate;
    };

    // Function to check for existing watermark in the request
    const checkExistingWatermark = async () => {
        if (!requestId || !currentUser) return;

        try {
            // Check if watermark result exists in private subcollection
            const watermarkResultDoc = await getDoc(
                doc(db, 'GenerationRequests', requestId, 'private', 'watermarkResult')
            );

            if (watermarkResultDoc.exists()) {
                const checkData = watermarkResultDoc.data();
                setHasExistingWatermark(checkData.summary?.hasWatermark || false);
            }
        } catch (error) {
            console.error("Error checking existing watermark:", error);
        }
    };

    // Function to run watermark check
    const runWatermarkCheck = async () => {
        if (!requestId || !requestData?.StravaId || !currentUser) {
            showCustomDialog(
                'info',
                t('ios.authRequiredTitle', 'Authentication Required'),
                t('ios.authRequiredMessage', 'Please sign in and ensure the map is linked to a Strava activity.'),
                'ti-info-circle'
            );
            return;
        }

        setIsCheckingWatermark(true);

        try {
            const checkWatermarksFunc = httpsCallable(functions, 'checkActivityWatermarks');
            const result = await checkWatermarksFunc({ requestId: requestId });

            setHasExistingWatermark(result.data.summary?.hasWatermark || false);

            if (result.data.summary?.hasWatermark) {
                showCustomDialog(
                    'success',
                    t('ios.foundTitle', 'We found it!'),
                    t('ios.foundMessage', 'Your map was found on Strava! A credit has been applied to your account.'),
                    'ti-check-circle'
                );
            } else {
                showCustomDialog(
                    'info',
                    t('ios.notFoundTitle', 'Map Not Found'),
                    t('ios.notFoundMessage', 'Map not detected yet. Make sure you\'ve uploaded your map image to your Strava activity and try again.'),
                    'ti-search'
                );
            }
        } catch (error) {
            console.error("Error running watermark check:", error);
            showCustomDialog(
                'error',
                t('ios.checkFailedTitle', 'Check Failed'),
                t('ios.checkFailedMessage', 'Failed to check for shared map. Please try again later.'),
                'ti-alert-circle'
            );
        } finally {
            setIsCheckingWatermark(false);
        }
    };

    useEffect(() => {
        // Get parameters from URL query string
        const searchParams = new URLSearchParams(location.search);
        const imageUrlParam = searchParams.get('imageUrl');
        const stravaIdParam = searchParams.get('stravaId');

        if (imageUrlParam) {
            setImageUrl(imageUrlParam);
        }

        if (stravaIdParam) {
            setStravaId(stravaIdParam);
        }
    }, [location.search, requestId]);

    // Add auth state listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
        });
        return () => unsubscribe();
    }, []);

    // Fetch request data
    useEffect(() => {
        const fetchRequestData = async () => {
            if (!requestId) return;

            try {
                const docRef = doc(db, "GenerationRequests", requestId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setRequestData(data);

                    // Update watermark status
                    setWatermarkCheckStatus(data.watermarkCheckStatus || null);
                    setWatermarkCheckError(data.watermarkCheckError || null);
                    setHasExistingWatermark(data.hasWatermark || false);

                    // Show watermark button if map is complete and has Strava ID
                    if (data.status === 'openai_processing_complete' && data.StravaId) {
                        setShowWatermarkButton(true);
                    }
                }
            } catch (error) {
                console.error("Error fetching request data:", error);
            }
        };

        fetchRequestData();
    }, [requestId]);

    // Check for existing watermark when we have both request data and user
    useEffect(() => {
        if (requestId && requestData?.StravaId && currentUser) {
            checkExistingWatermark();
        }
    }, [requestId, requestData?.StravaId, currentUser]);

    if (!imageUrl) {
        return (
            <div className="ios-download-container">
                <div className="ios-download-card">
                    <h1>{t('ios.errorTitle', 'Error')}</h1>
                    <p>{t('ios.imageMissing', 'Image URL is missing. Please try downloading again.')}</p>
                    <button onClick={() => navigate(-1)} className="ios-back-button">
                        ← {t('common.goBack', 'Go Back')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="ios-download-container">
            <div className="ios-download-card">
                <h1>{t('ios.saveYourMap', 'Save Your Mythic Map')}</h1>

                <div className="ios-image-container">
                    <img
                        src={imageUrl}
                        alt={t('ios.imageAlt', 'Your AI-generated map')}
                        className="ios-ai-image"
                    />
                    <div className="ios-touch-hint">
                        <div className="pulse-circle"></div>
                        <div className="finger-icon">👆</div>
                    </div>
                </div>

                <div className="ios-instructions">
                    <div className="instruction-step">
                        <div className="step-number">1</div>
                        <p>{t('ios.step1', 'Press and hold on the image above')}</p>
                    </div>

                    <div className="instruction-step">
                        <div className="step-number">2</div>
                        <p>{t('ios.step2', 'Tap "Save in Photos" from the menu')}</p>
                    </div>

                    {stravaId && (
                        <>
                            <div className="instruction-step">
                                <div className="step-number">3</div>
                                <p>{t('ios.step3', 'After saving, upload to your activity:')}</p>
                            </div>

                            <a
                                href={`https://www.strava.com/activities/${stravaId}/edit`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="strava-button"
                            >
                                {t('ios.goToStrava', 'Go to Strava Activity')}
                            </a>

                            <div className="strava-tip">
                                <div className="tip-icon">💡</div>
                                <p>{t('ios.stravaTip', 'In Strava, tap "Add Photos" and select the saved image from your photo library')}</p>
                            </div>
                        </>
                    )}
                </div>

                {/* Share Credit Check Section */}
                {showWatermarkButton && currentUser && requestData?.StravaId && requestData?.userId === currentUser.uid && (
                    <div className="watermark-check-section">
                        {hasExistingWatermark ? (
                            <div className="watermark-credit-applied">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                </svg>
                                <span>{t('ios.creditApplied', 'Credit applied for sharing this map!')}</span>
                            </div>
                        ) : watermarkCheckStatus === 'checking' ? (
                            <div className="watermark-check-in-progress">
                                <div className="loading-spinner-small"></div>
                                <span>{t('ios.checkingActivity', 'Checking your Strava activity...')}</span>
                            </div>
                        ) : watermarkCheckStatus === 'failed' ? (
                            <div className="watermark-check-error">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                                <span>{watermarkCheckError || t('ios.checkFailedGeneric', 'Failed to check for shared map')}</span>
                                <button
                                    onClick={runWatermarkCheck}
                                    className="watermark-check-button retry"
                                    style={{ marginTop: '10px' }}
                                >
                                    {t('ios.tryAgain', 'Try Again')}
                                </button>
                            </div>
                        ) : (
                            <div className="watermark-check-prompt">
                                <p>{t('ios.sharedPrompt', 'Shared it on Strava? Get a free credit on us')}</p>
                                <button
                                    onClick={runWatermarkCheck}
                                    disabled={isCheckingWatermark || watermarkCheckStatus === 'checking'}
                                    className="watermark-check-button"
                                >
                                    {isCheckingWatermark || watermarkCheckStatus === 'checking' ? (
                                        <>
                                            <div className="loading-spinner-small"></div>
                                            {t('ios.checking', 'Checking...')}
                                        </>
                                    ) : (
                                        t('ios.claimCredit', 'Claim your credit')
                                    )}
                                </button>
                                <small>{t('ios.earnPerMap', 'Earn 1 credit per map shared on Strava')}</small>
                            </div>
                        )}
                    </div>
                )}

                <button onClick={() => navigate(-1)} className="ios-back-button">
                    ← {t('common.goBack', 'Go Back')}
                </button>
            </div>

            {/* Custom Dialog */}
            {showDialog && (
                <div className="dialog-overlay" onClick={() => setShowDialog(false)}>
                    <div className="dialog-container" onClick={(e) => e.stopPropagation()}>
                        <div className={`dialog-content ${dialogContent.type}`}>
                            <div className="dialog-header">
                                {dialogContent.icon && (
                                    <svg className="dialog-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        {dialogContent.type === 'success' && <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>}
                                        {dialogContent.type === 'success' && <polyline points="22 4 12 14.01 9 11.01"></polyline>}
                                        {dialogContent.type === 'error' && <circle cx="12" cy="12" r="10"></circle>}
                                        {dialogContent.type === 'error' && <line x1="12" y1="8" x2="12" y2="12"></line>}
                                        {dialogContent.type === 'error' && <line x1="12" y1="16" x2="12.01" y2="16"></line>}
                                    </svg>
                                )}
                                <h3 className="dialog-title">{dialogContent.title}</h3>
                            </div>
                            <div className="dialog-body">
                                <div className="dialog-message">{dialogContent.message}</div>
                            </div>
                            <div className="dialog-actions">
                                <button
                                    onClick={() => setShowDialog(false)}
                                    className="dialog-button primary"
                                >
                                    {t('common.ok', 'OK')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default IOSDownloadPage;

